import { PROTOCOL_VERSION, type ServerEvent } from '@dijitalapp/shared';
import { api } from './api';
import { onResume } from './resume';

export type ConnectionState = 'connecting' | 'connected' | 'reconnecting' | 'offline';

type Listener = (event: ServerEvent) => void;

const CLOSE_SESSION_REVOKED = 4001;
const MAX_BACKOFF_MS = 30_000;
/** While someone looks at the app, a retry is never further off than this. */
const MAX_VISIBLE_BACKOFF_MS = 5_000;
/** The ticket request gives up after this (a phone just woken may hang a request for a minute or more). */
export const TICKET_TIMEOUT_MS = 5_000;
/** From the ticket to the server's hello: a socket that takes longer is given up and tried again. */
export const HELLO_TIMEOUT_MS = 6_000;
/** Back in front with the socket seemingly open: no answer to a ping within this means it is dead. */
export const PROBE_TIMEOUT_MS = 2_500;
/** An attempt still under way after this when the app comes back (or the network does) is started over. */
export const STALE_ATTEMPT_MS = 3_000;

const visible = () => typeof document === 'undefined' || document.visibilityState === 'visible';

/**
 * WebSocket client for the realtime gateway (REALTIME_PROTOCOL.md): ticket handshake, heartbeat,
 * reconnect with exponential backoff + jitter, and a `connected` hook the store uses to run cursor
 * sync for anything missed while disconnected.
 *
 * One attempt at a time (a generation number drops an attempt that was replaced), each step with a hard
 * timeout, and a socket believed dead is let go at once: a phone's dead connection can take half a minute to
 * report its close, and waiting for that kept the app on old messages.
 */
export class RealtimeClient {
  private ws: WebSocket | null = null;
  private listeners = new Set<Listener>();
  private stateListeners = new Set<(s: ConnectionState) => void>();
  private attempt = 0;
  private stopped = false;
  private heartbeat: ReturnType<typeof setInterval> | null = null;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private helloTimer: ReturnType<typeof setTimeout> | null = null;
  private lastFrameAt = 0;
  /** Frames received so far (a probe checks whether any came after its ping). */
  private frames = 0;
  private probe: ReturnType<typeof setTimeout> | null = null;
  /** Each attempt's number: an older attempt that finishes late changes nothing. */
  private generation = 0;
  /** When the attempt under way (ticket or handshake) began; 0 when none is. */
  private connectingSince = 0;
  private ticketAbort: AbortController | null = null;
  private offResume: () => void;
  state: ConnectionState = 'connecting';

  constructor(private readonly onSessionRevoked: () => void) {
    window.addEventListener('online', this.onOnline);
    window.addEventListener('offline', this.onOffline);
    this.offResume = onResume((away) => this.resume(away));
  }

  /**
   * Back in front. A phone may have frozen the app with its socket silently dead, so do not wait for the
   * heartbeat (up to a minute): reconnect at once if closed or still trying from before, otherwise ping and
   * reconnect if nothing answers within a moment. A reconnect runs the cursor sync, so whatever arrived
   * meanwhile shows up. `awayMs` 0: thawed without having been hidden (the probe runs then too).
   */
  resume(awayMs: number): void {
    this.check(awayMs === 0 || awayMs >= 1000);
  }

  /** A notification was tapped or the network came back: the connection is checked now, not at the next retry. */
  wake(): void {
    this.check(true);
  }

  private check(probe: boolean): void {
    if (this.stopped) return;
    if (!navigator.onLine) {
      this.drop();
      this.setState('offline');
      return;
    }
    if (this.connectingSince) {
      if (Date.now() - this.connectingSince > STALE_ATTEMPT_MS) this.reconnectNow();
      return;
    }
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      this.reconnectNow();
      return;
    }
    if (probe) this.probeNow(this.ws);
  }

  private probeNow(ws: WebSocket): void {
    if (this.probe) return;
    const before = this.frames;
    try {
      ws.send(JSON.stringify({ version: PROTOCOL_VERSION, cmd: 'ping' }));
    } catch {
      this.reconnectNow();
      return;
    }
    this.probe = setTimeout(() => {
      this.probe = null;
      if (this.ws === ws && this.frames === before) this.reconnectNow();
    }, PROBE_TIMEOUT_MS);
  }

  onEvent(l: Listener): () => void {
    this.listeners.add(l);
    return () => this.listeners.delete(l);
  }

  onState(l: (s: ConnectionState) => void): () => void {
    this.stateListeners.add(l);
    return () => this.stateListeners.delete(l);
  }

  private setState(s: ConnectionState) {
    if (this.state === s) return;
    this.state = s;
    this.stateListeners.forEach((l) => l(s));
  }

  /** Starts connecting unless an attempt is already under way or a socket is open (then it is that one). */
  async start(): Promise<void> {
    this.stopped = false;
    if (this.connectingSince || this.ws) return;
    if (!navigator.onLine) {
      this.setState('offline');
      return;
    }
    const generation = ++this.generation;
    this.connectingSince = Date.now();
    this.setState(this.attempt === 0 ? 'connecting' : 'reconnecting');
    const abort = new AbortController();
    this.ticketAbort = abort;
    let ticket: string;
    try {
      ticket = (await api<{ ticket: string }>('POST', '/realtime/ticket', undefined, { timeoutMs: TICKET_TIMEOUT_MS, signal: abort.signal })).ticket;
    } catch {
      if (generation !== this.generation) return;
      this.connectingSince = 0;
      this.ticketAbort = null;
      this.scheduleReconnect();
      return;
    }
    if (generation !== this.generation || this.stopped) return;
    this.ticketAbort = null;
    const scheme = location.protocol === 'https:' ? 'wss' : 'ws';
    const ws = new WebSocket(`${scheme}://${location.host}/api/v1/realtime/ws?ticket=${ticket}`);
    this.ws = ws;
    // No hello in time (a handshake stuck on a dead network): given up and tried again, never left "connecting".
    this.helloTimer = setTimeout(() => {
      this.helloTimer = null;
      if (this.ws !== ws || this.state === 'connected') return;
      this.drop();
      this.scheduleReconnect();
    }, HELLO_TIMEOUT_MS);

    ws.onmessage = (msg) => {
      if (this.ws !== ws) return;
      this.lastFrameAt = Date.now();
      this.frames++;
      let event: ServerEvent;
      try {
        event = JSON.parse(String(msg.data)) as ServerEvent;
      } catch {
        return;
      }
      if (event.event === 'hello') {
        this.attempt = 0;
        this.connectingSince = 0;
        this.clearHelloTimer();
        this.startHeartbeat(ws, event.payload.heartbeatIntervalMs);
        this.setState('connected');
      }
      if (event.event === 'server.draining') {
        // The server is going away: this socket is let go and the next one opened after the pause it asks for.
        this.drop();
        this.scheduleReconnect(event.payload.reconnectAfterMs);
      }
      // Unknown events are ignored by listeners (forward compatibility).
      this.listeners.forEach((l) => l(event));
    };

    ws.onclose = (e) => {
      if (this.ws !== ws) return;
      this.ws = null;
      this.connectingSince = 0;
      this.clearHelloTimer();
      this.stopHeartbeat();
      if (e.code === CLOSE_SESSION_REVOKED) {
        this.stopped = true;
        this.onSessionRevoked();
        return;
      }
      if (!this.stopped) this.scheduleReconnect();
    };
  }

  /** Fire-and-forget command (dropped while disconnected); `reqId` lets error frames be matched to it. */
  command(cmd: string, payload: Record<string, unknown>, reqId?: string): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ version: PROTOCOL_VERSION, cmd, ...(reqId ? { req_id: reqId } : {}), payload }));
    }
  }

  /** Lets go of the attempt under way and the socket, without waiting for its close (a dead one may take ~30 s). */
  private drop(): void {
    this.generation++;
    this.connectingSince = 0;
    this.ticketAbort?.abort();
    this.ticketAbort = null;
    this.clearHelloTimer();
    this.stopHeartbeat();
    if (this.probe) clearTimeout(this.probe);
    this.probe = null;
    const ws = this.ws;
    this.ws = null;
    if (ws) {
      ws.onmessage = null;
      ws.onclose = null;
      try {
        ws.close();
      } catch {
        /* already closing */
      }
    }
  }

  /** Starts over now, from the first attempt (no backoff). */
  private reconnectNow(): void {
    if (this.stopped) return;
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.retryTimer = null;
    this.drop();
    this.attempt = 0;
    void this.start();
  }

  private clearHelloTimer() {
    if (this.helloTimer) clearTimeout(this.helloTimer);
    this.helloTimer = null;
  }

  private startHeartbeat(ws: WebSocket, intervalMs: number) {
    this.stopHeartbeat();
    this.lastFrameAt = Date.now();
    this.heartbeat = setInterval(() => {
      if (this.ws !== ws) return;
      if (Date.now() - this.lastFrameAt > intervalMs * 2) {
        // Two silent intervals: the connection is dead even if the browser has not noticed.
        this.reconnectNow();
        return;
      }
      try {
        ws.send(JSON.stringify({ version: PROTOCOL_VERSION, cmd: 'ping' }));
      } catch {
        this.reconnectNow();
      }
    }, intervalMs);
  }

  private stopHeartbeat() {
    if (this.heartbeat) clearInterval(this.heartbeat);
    this.heartbeat = null;
  }

  private scheduleReconnect(afterMs?: number) {
    if (this.stopped) return;
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.retryTimer = null;
    if (!navigator.onLine) {
      this.setState('offline');
      return;
    }
    this.setState('reconnecting');
    this.attempt += 1;
    const base = Math.min(visible() ? MAX_VISIBLE_BACKOFF_MS : MAX_BACKOFF_MS, 500 * 2 ** Math.min(this.attempt, 6));
    const delay = afterMs ?? base / 2 + Math.random() * (base / 2);
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      void this.start();
    }, delay);
  }

  private onOnline = () => {
    if (this.stopped) return;
    this.attempt = 0;
    this.check(true);
  };

  private onOffline = () => {
    this.drop();
    this.setState('offline');
  };

  stop(): void {
    this.stopped = true;
    this.offResume();
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.retryTimer = null;
    this.drop();
    window.removeEventListener('online', this.onOnline);
    window.removeEventListener('offline', this.onOffline);
  }
}
